import {
  AsyncThunk,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from './store';
import { getCategories } from '../api/api';
import { Category } from '../types/types';
import { getRouterPathToContentIndex } from '../utilities/Links';

export const categoryStateEnum = {
  INITIAL: 'initial',
  LOADING: 'loading',
  ERROR: 'error',
  READY: 'ready',
} as const;

type CategoryStateType =
  (typeof categoryStateEnum)[keyof typeof categoryStateEnum];

export interface CategoriesState {
  value: Category[];
  state: CategoryStateType;
}

const initialState: CategoriesState = {
  value: [],
  state: categoryStateEnum.INITIAL,
};

export const initCategoriesAsync: AsyncThunk<Category[], void, object> =
  createAsyncThunk('categories/initCategoriesAsync', async () => {
    const response = await getCategories();
    return response.map((category: string) => ({
      name: category,
      path: getRouterPathToContentIndex(category),
    }));
  });

const categoriesSlice = createSlice({
  initialState,
  name: 'categories',
  reducers: {
    setCategories(state, action: PayloadAction<Category[]>) {
      state.value = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initCategoriesAsync.pending, (state) => {
      state.state = categoryStateEnum.LOADING;
    });
    builder.addCase(
      initCategoriesAsync.fulfilled,
      (state, action: PayloadAction<Category[]>) => {
        state.value = action.payload;
        state.state = categoryStateEnum.READY;
      },
    );
    builder.addCase(initCategoriesAsync.rejected, (state) => {
      state.state = categoryStateEnum.ERROR;
    });
  },
});

export const { setCategories } = categoriesSlice.actions;

export const selectCategories = (state: RootState) => state.categories.value;
export const selectState = (state: RootState) => state.categories.state;

export default categoriesSlice.reducer;
